import React, { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';
import { scorecardData } from '../data/newModules';
import AnimatedSection from '../components/common/AnimatedSection';
import SocialShare from '../components/common/SocialShare';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  CheckCircle, AlertTriangle, XCircle, ArrowRight, ArrowLeft,
  Share2, Bell, Calendar, Tag, ChevronDown, ChevronUp, Filter
} from 'lucide-react';

const ScorecardPage = () => {
  const { language } = useLanguage();
  const { isDark } = useTheme();
  const content = scorecardData[language] || scorecardData.fr;
  const [selectedPosition, setSelectedPosition] = useState(null);
  const [filterTag, setFilterTag] = useState(null);
  const [expandedItem, setExpandedItem] = useState(null);

  const positionConfig = {
    supported: { 
      icon: CheckCircle, 
      bgColor: isDark ? 'bg-green-500/10' : 'bg-green-50',
      borderColor: 'border-green-500',
      textColor: 'text-green-600 dark:text-green-400',
      badgeColor: isDark ? 'bg-green-500/20 text-green-400' : 'bg-green-100 text-green-700'
    },
    amended: { 
      icon: AlertTriangle, 
      bgColor: isDark ? 'bg-yellow-500/10' : 'bg-yellow-50',
      borderColor: 'border-yellow-500',
      textColor: 'text-yellow-600 dark:text-yellow-400',
      badgeColor: isDark ? 'bg-yellow-500/20 text-yellow-400' : 'bg-yellow-100 text-yellow-700'
    },
    opposed: { 
      icon: XCircle, 
      bgColor: isDark ? 'bg-red-500/10' : 'bg-red-50',
      borderColor: 'border-red-500',
      textColor: 'text-red-600 dark:text-red-400',
      badgeColor: isDark ? 'bg-red-500/20 text-red-400' : 'bg-red-100 text-red-700'
    }
  };

  const filteredItems = content.items.filter(item => {
    if (selectedPosition && item.position !== selectedPosition) return false;
    if (filterTag && !item.tags.includes(filterTag)) return false;
    return true;
  });

  const allTags = [...new Set(content.items.flatMap(item => item.tags))];

  const groupedItems = {
    supported: filteredItems.filter(i => i.position === 'supported'),
    amended: filteredItems.filter(i => i.position === 'amended'),
    opposed: filteredItems.filter(i => i.position === 'opposed')
  };

  return (
    <div className="pt-20">
      {/* Hero */}
      <section className="py-16 bg-gradient-to-br from-[#5B2D8E] to-[#7C4DAF] relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 right-10 w-64 h-64 bg-white rounded-full blur-3xl" />
        </div>
        <div className="container-custom relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <AnimatedSection>
              <h1 className="heading-1 text-white mb-4">{content.title}</h1>
              <p className="text-xl text-white/80 mb-6">{content.subtitle}</p>
              <p className="text-white/60">{content.description}</p>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Legend */}
      <section className={`py-8 border-b ${isDark ? 'bg-[#1C1917] border-white/10' : 'bg-white border-stone-200'}`}>
        <div className="container-custom">
          <div className="flex flex-wrap justify-center gap-4">
            {Object.entries(content.categories).map(([key, cat]) => {
              const config = positionConfig[key];
              const Icon = config.icon;
              const isActive = selectedPosition === key;
              return (
                <button
                  key={key}
                  onClick={() => setSelectedPosition(isActive ? null : key)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-full transition-all ${
                    isActive 
                      ? `${config.badgeColor} ring-2 ring-offset-2 ${isDark ? 'ring-offset-[#1C1917]' : 'ring-offset-white'} ring-current`
                      : isDark ? 'bg-white/10 text-stone-300 hover:bg-white/20' : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  <span className="font-medium">{cat.label}</span>
                </button>
              );
            })}
          </div>
          
          {/* Tag Filter */}
          <div className="flex flex-wrap justify-center gap-2 mt-4">
            <span className={`text-sm ${isDark ? 'text-stone-400' : 'text-stone-500'}`}>
              <Filter className="w-4 h-4 inline mr-1" /> Tags:
            </span>
            {allTags.map(tag => (
              <button
                key={tag}
                onClick={() => setFilterTag(filterTag === tag ? null : tag)}
                className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                  filterTag === tag
                    ? 'bg-[#5B2D8E] text-white'
                    : isDark ? 'bg-white/10 text-stone-300 hover:bg-white/20' : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
                }`}
              >
                {tag}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Content - 3 Columns on Desktop */}
      <section className={`section ${isDark ? 'bg-[#0C0A09]' : 'bg-[#FAFAF9]'}`}>
        <div className="container-custom">
          <div className="grid lg:grid-cols-3 gap-6">
            {Object.entries(groupedItems).map(([position, items]) => {
              const config = positionConfig[position];
              const Icon = config.icon;
              const category = content.categories[position];
              
              return (
                <div key={position} className="space-y-4">
                  {/* Column Header */}
                  <div className={`p-4 rounded-xl ${config.bgColor} border-l-4 ${config.borderColor}`}>
                    <div className="flex items-center gap-2">
                      <Icon className={`w-6 h-6 ${config.textColor}`} />
                      <h2 className={`text-lg font-bold ${config.textColor}`}>{category.label}</h2>
                      <span className={`ml-auto px-2 py-0.5 rounded-full text-xs ${config.badgeColor}`}>
                        {items.length}
                      </span>
                    </div>
                  </div>

                  {/* Items */}
                  {items.map((item, index) => (
                    <AnimatedSection key={item.id} delay={index * 0.1}>
                      <motion.div
                        className={`rounded-xl overflow-hidden ${isDark ? 'bg-[#1C1917]' : 'bg-white'} border ${isDark ? 'border-white/10' : 'border-stone-200'}`}
                        layout
                      >
                        <div className="p-4">
                          {/* Tags */}
                          <div className="flex flex-wrap gap-1 mb-2">
                            {item.tags.map(tag => (
                              <span key={tag} className={`px-2 py-0.5 rounded text-xs ${isDark ? 'bg-white/10 text-stone-400' : 'bg-stone-100 text-stone-500'}`}>
                                {tag}
                              </span>
                            ))}
                          </div>
                          
                          {/* Title */}
                          <h3 className="font-semibold text-theme-primary mb-2">{item.title}</h3>
                          
                          {/* Date */}
                          <div className={`flex items-center gap-1 text-xs mb-3 ${isDark ? 'text-stone-500' : 'text-stone-400'}`}>
                            <Calendar className="w-3 h-3" />
                            {new Date(item.date).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
                          </div>
                          
                          {/* Summary */}
                          <p className="text-sm text-theme-secondary mb-3">{item.summary}</p>

                          {/* Expand/Collapse */}
                          <button
                            onClick={() => setExpandedItem(expandedItem === item.id ? null : item.id)}
                            className={`flex items-center gap-1 text-sm font-medium ${isDark ? 'text-[#FFC107]' : 'text-[#5B2D8E]'}`}
                          >
                            {expandedItem === item.id ? (
                              <>Réduire <ChevronUp className="w-4 h-4" /></>
                            ) : (
                              <>Voir le détail <ChevronDown className="w-4 h-4" /></>
                            )}
                          </button>

                          {/* Expanded Content */}
                          <AnimatePresence>
                            {expandedItem === item.id && (
                              <motion.div
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: 'auto', opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                className="overflow-hidden"
                              >
                                <div className={`mt-4 pt-4 border-t ${isDark ? 'border-white/10' : 'border-stone-200'} space-y-3`}>
                                  {/* Why */}
                                  <div>
                                    <h4 className={`text-xs font-semibold uppercase tracking-wider mb-1 ${isDark ? 'text-stone-400' : 'text-stone-500'}`}>
                                      Pourquoi
                                    </h4>
                                    <p className="text-sm text-theme-secondary">{item.why}</p>
                                  </div>

                                  {/* Proposal (if amended/opposed) */}
                                  {item.proposal && (
                                    <div>
                                      <h4 className={`text-xs font-semibold uppercase tracking-wider mb-1 ${isDark ? 'text-stone-400' : 'text-stone-500'}`}>
                                        Ce que nous proposons
                                      </h4>
                                      <p className="text-sm text-theme-secondary">{item.proposal}</p>
                                    </div>
                                  )}

                                  {/* Impact */}
                                  <div>
                                    <h4 className={`text-xs font-semibold uppercase tracking-wider mb-1 ${isDark ? 'text-stone-400' : 'text-stone-500'}`}>
                                      Impact attendu
                                    </h4>
                                    <p className="text-sm text-theme-secondary">{item.impact}</p>
                                  </div>

                                  {/* Social Share */}
                                  <div className="pt-3">
                                    <SocialShare
                                      title={item.title}
                                      text={`Position ADA : ${item.title} — ${item.summary}`}
                                      url={`${window.location.origin}/scorecard#${item.id}`}
                                      hashtags={['ADACI', 'CotedIvoire', content.categories[item.position].label.replace(/\s/g, '')]}
                                      compact={false}
                                    />
                                  </div>
                                </div>
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </div>
                      </motion.div>
                    </AnimatedSection>
                  ))}

                  {items.length === 0 && (
                    <div className={`p-8 rounded-xl text-center ${isDark ? 'bg-[#1C1917]' : 'bg-white'} border ${isDark ? 'border-white/10' : 'border-stone-200'}`}>
                      <p className="text-theme-muted text-sm">Aucun élément dans cette catégorie</p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className={`py-16 ${isDark ? 'bg-[#1C1917]' : 'bg-[#F5F0E8]'}`}>
        <div className="container-custom">
          <div className="max-w-2xl mx-auto text-center">
            <AnimatedSection>
              <Bell className={`w-12 h-12 mx-auto mb-4 ${isDark ? 'text-[#FFC107]' : 'text-[#5B2D8E]'}`} />
              <h2 className="heading-3 mb-4">Restez informé</h2>
              <p className="text-theme-secondary mb-6">
                Recevez nos prochaines positions dès leur publication.
              </p>
              <Link to="/rejoindre" className="btn-primary">
                {content.cta.subscribe}
                <ArrowRight className="w-5 h-5" />
              </Link>
            </AnimatedSection>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ScorecardPage;
